import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  ButtonOwnProps,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CardWrapper from './CardWrapper';
import { TEMPLATE_PLACEHOLDER_URL } from '../../../constants/marketing';

interface Props {
  id: number | string;
  name: string;
  isSelected?: boolean;
  isPending?: boolean;
  previewUrl: string | null;
  imageStyle?: React.CSSProperties;
  buttons?: {
    text: string;
    onClick: () => void | Promise<void>;
    variant?: ButtonOwnProps['variant'];
    color?: ButtonOwnProps['color'];
    hidden?: boolean;
    isLoading?: boolean;
  }[];
}

const buttonStyle: SxProps<Theme> = {
  width: '100%',
  height: '40px',
  fontSize: '14px',
  borderRadius: '10px',
  boxShadow: 2,
};

function TemplateCard({
  id,
  name,
  previewUrl,
  isPending,
  isSelected,
  imageStyle,
  buttons,
}: Props) {
  const theme = useTheme();

  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setIsError(false);
  }, [previewUrl]);

  const actionsId = `actions-${id}`;

  const visibleStyle = {
    opacity: 1,
    pointerEvents: 'auto',
  };

  const filteredButtons = useMemo(
    () => buttons?.filter(({ hidden }) => !hidden),
    [buttons],
  );

  return (
    <CardWrapper isOutlined={isSelected}>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        height="100%"
        overflow="hidden"
        boxSizing="border-box"
        sx={{
          '&:hover': {
            [`#${actionsId}`]: visibleStyle,
          },
        }}>
        <Box
          boxSizing="border-box"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flex={1}
          overflow="hidden">
          <img
            src={isError || !previewUrl ? TEMPLATE_PLACEHOLDER_URL : previewUrl}
            alt="Template"
            style={{
              objectFit: 'contain',
              width: '100%',
              height: '100%',
              padding: '1rem',
              margin: 'auto',
              boxSizing: 'border-box',
              ...(imageStyle || {}),
            }}
            onError={() => setIsError(true)}
          />
        </Box>
        <Box
          boxSizing="border-box"
          flexShrink={0}
          mt="auto"
          p={2.5}
          borderTop={`1px solid ${theme.palette.darkerLightGrey.main}`}>
          <Typography variant="body1" noWrap title={name || 'Unnamed Template'}>
            {name || 'Unnamed Template'}
          </Typography>
        </Box>

        {!!filteredButtons?.length && (
          <Box
            id={actionsId}
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgcolor="rgba(255, 255, 255, 0.9)"
            sx={{
              pointerEvents: 'none',
              opacity: 0,
              transition: 'opacity 0.2s',
              ...(isPending ? visibleStyle : {}),
            }}>
            <Stack
              gap={1}
              m="auto"
              width="max-content"
              height="100%"
              alignItems="center"
              justifyContent="center">
              {filteredButtons?.map(
                ({ text, onClick, variant, color, isLoading }) => (
                  <LoadingButton
                    key={`${text}-${id}-${variant}-${color}`}
                    disabled={isPending || isLoading}
                    variant={variant || 'light'}
                    color={color || 'info'}
                    sx={buttonStyle}
                    onClick={onClick}
                    loading={isLoading}>
                    <span>{text}</span>
                  </LoadingButton>
                ),
              )}
            </Stack>
          </Box>
        )}
      </Box>
    </CardWrapper>
  );
}

export default TemplateCard;
